/**
 * useServiceCatalog hooks — Issue #331.
 *
 * Typed hooks for service catalog list/detail/create/update/compliance/enable
 * endpoints, built on the generated @jinzhuan/api-client types.
 */
import { useCallback, useState } from 'react';
import {
  createApiClient,
  ApiError as ApiClientError,
} from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type ServiceCatalogSummaryDto =
  ApiComponents['schemas']['ServiceCatalogSummaryDto'];
export type ServiceCatalogDetailDto =
  ApiComponents['schemas']['ServiceCatalogDetailDto'];
export type ServiceCatalogListResponseDto =
  ApiComponents['schemas']['ServiceCatalogListResponseDto'];
export type CreateServiceCatalogDto =
  ApiComponents['schemas']['CreateServiceCatalogDto'];
export type UpdateServiceCatalogDto =
  ApiComponents['schemas']['UpdateServiceCatalogDto'];
export type UpdateComplianceStatusDto =
  ApiComponents['schemas']['UpdateComplianceStatusDto'];
export type ToggleEnabledDto = ApiComponents['schemas']['ToggleEnabledDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, status?: number): ApiError {
  const effective =
    status ?? (err instanceof ApiClientError ? err.status : undefined);
  if (effective === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (effective === 409) {
    return { message: '数据已被其他用户修改，请刷新后重试', code: 'CONFLICT' };
  }
  if (effective === 403) {
    return { message: '没有权限执行该操作', code: 'FORBIDDEN' };
  }
  if (err instanceof Error) return { message: err.message };
  return { message: String(err) };
}

export function useServiceCatalogList() {
  const [data, setData] = useState<ServiceCatalogListResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchList = useCallback(
    async (filter?: {
      category?: string;
      complianceStatus?: string;
      enabled?: boolean;
      search?: string;
      page?: number;
      limit?: number;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const query: Record<string, string | number | boolean> = {};
        if (filter?.category) query.category = filter.category;
        if (filter?.complianceStatus)
          query.complianceStatus = filter.complianceStatus;
        if (filter?.enabled !== undefined) query.enabled = filter.enabled;
        if (filter?.search) query.search = filter.search;
        if (filter?.page) query.page = filter.page;
        if (filter?.limit) query.limit = filter.limit;
        const { data: ok, error: apiErr, response } = await apiClient.GET(
          '/api/v1/service-catalogs',
          { params: { query } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setData(ok ?? null);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { data, loading, error, fetchList };
}

export function useServiceCatalogActions() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const create = useCallback(async (dto: CreateServiceCatalogDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/service-catalogs',
        { body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const update = useCallback(
    async (id: string, dto: UpdateServiceCatalogDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT(
          '/api/v1/service-catalogs/{id}',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateComplianceStatus = useCallback(
    async (id: string, dto: UpdateComplianceStatusDto) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/service-catalogs/{id}/compliance-status',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const setEnabled = useCallback(async (id: string, dto: ToggleEnabledDto) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.POST(
        '/api/v1/service-catalogs/{id}/enable',
        { params: { path: { id } }, body: dto },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    create,
    update,
    updateComplianceStatus,
    setEnabled,
  };
}
